import { CurrentUserDetailsDTO, UserType, userTypeOf } from '@luminovo/api-contracts';
import { Permission, useProtected } from '@luminovo/auth';
import { isProductionEnvironment } from '@luminovo/commons';
import { useSuspenseHttpQuery } from '@luminovo/data-stack';
import * as React from 'react';

type FeatureFlags = {
    isClosedBetaTester: boolean;
    hasAccessToCustomerPortal: boolean;
};

export type UserDetails = CurrentUserDetailsDTO & { userType: UserType } & FeatureFlags;
/** Keep this a pure read: it's a hook with ~120 call sites; session side effects belong in `AuthenticatedApp`. */
export function useCurrentUserDetailsContext(): UserDetails {
    const { data: details } = useSuspenseHttpQuery(
        'GET /my/details',
        {},
        {
            staleTime: Infinity,
            select: (response) => response.data,
        },
    );

    return React.useMemo(() => userDetailsOf(details), [details]);
}

/** The user `GET /my/details` describes, with their user type: a supplier's, a customer's, or the EMS's own. */
export function userDetailsOf(details: CurrentUserDetailsDTO): UserDetails {
    return {
        ...details,
        userType: userTypeOf(details),
        isClosedBetaTester: details.organization.is_closed_beta_tester || !isProductionEnvironment(),
        hasAccessToCustomerPortal: details.organization.has_access_to_customer_portal,
    };
}

/** @deprecated Use `useCurrentUserDetailsContext`. */
export function useIsCustomer(): boolean {
    const userTypes = useUserType();
    return userTypes === UserType.Customer;
}

export function useUserType(): UserType {
    const { userType } = useCurrentUserDetailsContext();
    return userType;
}

export function useIsSupplierPortalUser(): boolean {
    return useUserType() === UserType.Supplier;
}

export const ADMIN_PERMISSIONS: Permission[] = ['edit:organization_settings', 'edit:user:organization'];

/**
 * Helper function to check if the logged-in user is an admin
 */
export function useIsAdminUser(): boolean {
    return useProtected({
        requiredPermissions: ADMIN_PERMISSIONS,
        fallback: false,
        value: true,
    });
}
